import { CheckCircle2, Copy, Landmark, PartyPopper, PlusCircle, RotateCcw, XCircle } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { parseDateInput } from "@/shared/utils/dateTimeHelpers";

import { usePayinLiveStatus } from "../hooks/usePayinLiveStatus";
import type { InitiatePayinResponse } from "../services/paymentsPayin.service";
import { payinOutcome } from "../utils/payinStatusProgress";

export interface PayinSpeiSuccessScreenProps {
  result: InitiatePayinResponse;
  onNewPayin: () => void;
}

/**
 * Success screen for a México / SPEI runtime payin.
 *
 * The collection instrument here is not a QR but a **CLABE minted for this payin**, with an expiry: the payer
 * transfers to it from their bank and the provider webhook matches the deposit back by that number. So the
 * screen leads with the CLABE, big and copyable, instead of an image.
 *
 * Stays live while open — `usePayinLiveStatus` (socket `payin:updated` + polling fallback) flips it to "paid"
 * the moment the webhook settles the payin, exactly like the Bre-B screen.
 */
export function PayinSpeiSuccessScreen({ result, onNewPayin }: PayinSpeiSuccessScreenProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const [copied, setCopied] = useState(false);
  const liveStatus = usePayinLiveStatus(result.id, { expiresAt: result.expires_at });
  // Every final status hides the CLABE, REVERSED included: offering it again would invite a second payment.
  const outcome = payinOutcome(liveStatus);

  const money = (value: string | null | undefined) => formatCurrencyAmount(value, "MXN", activeLanguage);

  const copyClabe = async () => {
    if (!result.clabe) return;
    try {
      await navigator.clipboard.writeText(result.clabe);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // noop
    }
  };

  // Bitso reports the expiry as a plain date ("2026-08-24"), so this renders the day only — formatting it
  // with a time component would print an invented midnight. It is a calendar date, not an instant:
  // `new Date()` reads it as UTC midnight, which west of UTC (Bogotá, Mexico City) is the previous day.
  const expiresLabel = result.expires_at
    ? parseDateInput(result.expires_at).toLocaleDateString(activeLanguage, { dateStyle: "medium" })
    : null;

  return (
    <div className="mx-auto max-w-md space-y-4">
      {outcome === "paid" && (
        <ColocaAlert
          variant="success"
          icon={PartyPopper}
          heading={t("spei_paid_title")}
          text={t("spei_paid_text")}
        />
      )}
      {outcome === "refused" && (
        <ColocaAlert variant="destructive" icon={XCircle} text={t("spei_rejected_text")} />
      )}
      {outcome === "reversed" && (
        <ColocaAlert
          variant="warning"
          icon={RotateCcw}
          heading={t("payin_reversed_title")}
          text={t("payin_reversed_text")}
        />
      )}

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex items-start gap-3 border-b border-border bg-primary/5 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary">
            <CheckCircle2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-primary">{t("spei_success_title")}</p>
            <p className="mt-0.5 font-mono text-xs text-muted-foreground">
              {t("breb_success_id")} · {result.id}
            </p>
            <p className="mt-1.5 text-sm text-foreground">
              {t("breb_success_amount")}{" "}
              <strong className="font-mono">{money(result.gross_amount)} MXN</strong>
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("breb_success_fee")}:{" "}
              <span className="font-mono font-medium text-foreground">
                {Number(result.fee_total) === 0
                  ? t("breb_success_fee_free")
                  : `${money(result.fee_total)} MXN`}
              </span>
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("breb_success_net")}:{" "}
              <span className="font-mono font-medium text-foreground">{money(result.net_amount)} MXN</span>
            </p>
          </div>
        </div>

        {/* The CLABE is the instrument — pointless once the transfer settled or can no longer arrive. */}
        {result.clabe && outcome === "open" && (
          <div className="space-y-3 p-4">
            <div className="space-y-3 rounded-xl border border-border bg-background p-4">
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                <Landmark className="h-3.5 w-3.5" aria-hidden="true" />
                {t("spei_success_clabe_label")}
              </p>
              <p className="break-all text-center font-mono text-2xl font-bold tracking-wider text-foreground">
                {result.clabe}
              </p>
              <ColocaButton
                variant="secondary"
                className="w-full"
                icon={copied ? CheckCircle2 : Copy}
                onClick={copyClabe}
              >
                {copied ? t("breb_success_copied") : t("spei_success_copy_button")}
              </ColocaButton>
              {expiresLabel && (
                <p className="text-center text-xs text-muted-foreground">
                  {t("breb_success_expires")} {expiresLabel}
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t("spei_success_hint")}</p>
          </div>
        )}
      </div>

      <ColocaButton variant="primary" icon={PlusCircle} className="w-full" onClick={onNewPayin}>
        {t("breb_success_new")}
      </ColocaButton>
    </div>
  );
}
